import { useState } from 'react';
import { createPortal } from 'react-dom';
import { type Character, authApi } from '../../auth/authApi';
const profileIcon = '/icons/profile.svg';
import styles from './CharacterStatsModal.module.css';
import { useTranslation } from '../../i18n/useTranslation';
import LoadoutEditor from './LoadoutEditor';
import { useDockDrag } from './useDockDrag';

interface Props {
  character: Character;
  onClose: () => void;
  classColor: string;
  token?: string;
  /** Aba inicial: 'stats' (padrão) ou 'loadout' (barra de batalha). */
  initialTab?: 'stats' | 'loadout';
  /** Janela "encaixada" abaixo do painel da cidade (Hub) — sem escurecer o fundo. */
  dock?: boolean;
  dockTop?: number;
  onUpdate?: (char: Character) => void;
}

export default function CharacterStatsModal({ character, onClose, classColor, token, initialTab = 'stats', dock = false, dockTop = 200, onUpdate }: Props) {
  const [tab, setTab] = useState<'stats' | 'loadout'>(initialTab);
  const { t, language } = useTranslation();
  const dragCtl = useDockDrag(true);
  const stats = character.class_data as any; // Using any or we can update the interface later

  const [unspentPoints, setUnspentPoints] = useState(character.unspentAttributePoints || 0);
  const [attributes, setAttributes] = useState(character.attributes || {
    strength: 0, intelligence: 0, agility: 0, luck: 0, wisdom: 0, initiative: 0, dodge: 0, block: 0
  });
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Todas as características usam a chave `attr.<nome>` (inclui
  // initiative/dodge/block, que antes ficavam sem tradução).
  const translateAttr = (attr: string) => t(`attr.${attr}` as any) || attr;

  // Nome/descrição da classe no idioma do usuário. A descrição do banco é
  // escrita em PT — em PT ela tem prioridade; nos outros idiomas usa a chave.
  const className = t(`class.${character.class}` as any) || stats?.display_name || character.class;
  const classDescKey = t(`class.${character.class}.desc` as any);
  const classDesc = language === 'pt'
    ? (stats?.description || classDescKey)
    : (classDescKey || stats?.description);


  // Calculate fake Level and XP based on totalPoints
  const xp = character.totalPoints || 0;
  // Simple formula: Level 1 requires 100 XP, Level 2 requires 200 XP, etc.
  const calculateLevel = (currentXp: number) => {
    let level = 1;
    let requiredXp = 100;
    let accumulatedXp = 0;
    
    while (currentXp >= accumulatedXp + requiredXp) {
      accumulatedXp += requiredXp;
      level++;
      requiredXp = Math.pow(level, 2) * 50 + 50;
    }
    
    const xpInCurrentLevel = currentXp - accumulatedXp;
    const progressPercent = Math.min(100, Math.max(0, (xpInCurrentLevel / requiredXp) * 100));
    
    return { level, xpInCurrentLevel, requiredXp, progressPercent };
  };

  const { level, xpInCurrentLevel } = calculateLevel(xp);

  // If backend returns level/XP, use it. Otherwise fallback to the old calculation.
  const displayLevel = character.level ?? level;
  const displayXpInCurrentLevel = character.currentXp ?? xpInCurrentLevel;
  const displayRequiredXp = Math.pow(displayLevel, 2) * 50 + 50;
  const displayProgressPercent = (displayXpInCurrentLevel / displayRequiredXp) * 100;

  // If sprite already has .png (like unique filenames), use it. Otherwise, append .png
  const spriteFileName = stats?.sprite || character.class;
  const spriteUrl = `/sprites/${spriteFileName.endsWith('.png') ? spriteFileName : spriteFileName + '.png'}`;
  
  const spriteConfig = stats?.sprite_config || { cols: 3, rows: 8, scale: 1.0, offsetX: 0, offsetY: 0 };
  const avatarSize = dock ? 52 : 80;

  const handleUpgradeAttribute = async (attr: string) => {
    if (unspentPoints <= 0 || isUpgrading) return;
    
    setIsUpgrading(true);
    try {
      const token = localStorage.getItem('darkage_token') || sessionStorage.getItem('darkage_token');
      if (!token) return;

      const res = await authApi.upgradeAttribute(token, attr);
      if (res.success) {
        setUnspentPoints(res.unspent);
        setAttributes(prev => ({ ...prev, [attr]: res.value }));
        if (onUpdate) {
          onUpdate({
            ...character,
            unspentAttributePoints: res.unspent,
            attributes: { ...attributes, [attr]: res.value }
          });
        }
      }
    } catch (err) {
      console.error('Failed to upgrade attribute:', err);
    } finally {
      setIsUpgrading(false);
    }
  };

  return createPortal(
    <div
      className={dock ? styles.dock : styles.overlay}
      data-dock-window={dock ? '' : undefined}
      onClick={dock ? undefined : onClose}
      style={dock ? ({ '--dock-top': `${dockTop}px`, ...dragCtl.style } as React.CSSProperties) : undefined}
    >
      <div
        className={dock ? `${styles.modal} ${styles.dockModal}` : styles.modal}
        onClick={e => e.stopPropagation()}
        style={{ '--accent-color': classColor } as React.CSSProperties}
      >
        {/* Glow effect in background */}
        <div className={styles.glowBg} />

        <div
          className={`${styles.header} ${dock ? styles.dockDragHandle : ''}`}
          onPointerDown={dock ? dragCtl.onHeaderPointerDown : undefined}
        >
          <div className={styles.headerLeft}>
            <img src={profileIcon} alt="" className={styles.headerImg} />
            <h2 className={styles.title}>{t('profile.title')}</h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label={t('common.close' as any) || 'Fechar'}>✕</button>
        </div>

        {/* Abas */}
        <div className={styles.tabBar}>
          <button
            className={`${styles.tabBtn} ${tab === 'stats' ? styles.tabBtnActive : ''}`}
            onClick={() => setTab('stats')}
          >📊 {t('profile.tabStats' as any) || 'Atributos'}</button>
          <button
            className={`${styles.tabBtn} ${tab === 'loadout' ? styles.tabBtnActive : ''}`}
            onClick={() => setTab('loadout')}
          >⚔️ {t('command.configTitle' as any) || 'Barra de Batalha'}</button>
        </div>

        {tab === 'loadout' ? (
          <div className={styles.body}>
            {token
              ? <LoadoutEditor token={token} />
              : <p style={{ color: '#a09070', fontSize: 13 }}>{t('profile.invalidSession' as any)}</p>}
          </div>
        ) : (
        <div className={styles.body}>
          {/* Identity Section */}
          <div className={styles.identityContainer}>
            <div className={styles.avatarWrapper}>
              <div className={styles.avatarPlaceholder} style={{ borderColor: classColor, overflow: 'hidden', position: 'relative', width: avatarSize, height: avatarSize }}>
                <img src={spriteUrl} alt={character.name}
                  style={{ 
                    position: 'absolute', 
                    top: spriteConfig.offsetY, 
                    left: spriteConfig.offsetX, 
                    width: `calc(${avatarSize}px * ${spriteConfig.cols})`, 
                    height: 'auto', 
                    imageRendering: 'pixelated',
                    transform: `scale(${spriteConfig.scale})`,
                    transformOrigin: 'top left'
                  }} 
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
              <div className={styles.levelBadge} style={{ background: classColor }}>
                Lvl {displayLevel}
              </div>
            </div>

            <div className={styles.identityInfo}>
              <h3 className={styles.charName}>{character.name}</h3>
              <span className={styles.charClass} style={{ color: classColor }}>
                {className}
              </span>

              {/* XP Bar */}
              <div className={styles.xpContainer}>
                <div className={styles.xpHeader}>
                  <span className={styles.xpLabel}>{t('profile.experience')}</span>
                  <span className={styles.xpValues}>{displayXpInCurrentLevel} / {displayRequiredXp} XP</span>
                </div>
                <div className={styles.xpBarTrack}>
                  <div className={styles.xpBarFill} style={{ width: `${displayProgressPercent}%`, background: classColor }} />
                  <div className={styles.xpBarGlow} style={{ width: `${displayProgressPercent}%`, background: classColor }} />
                </div>
              </div>
            </div>
          </div>

          <p className={styles.charDesc}>{classDesc}</p>

          {/* Combat History */}
          <div className={styles.historyRow}>
            <div className={styles.historyItem}>
              <span className={styles.historyLabel}>{t('profile.wins')}</span>
              <span className={styles.historyValWin}>{character.wins ?? 0}</span>
            </div>
            <div className={styles.historyItem}>
              <span className={styles.historyLabel}>{t('profile.losses')}</span>
              <span className={styles.historyValLoss}>{character.losses ?? 0}</span>
            </div>
            <div className={styles.historyItem}>
              <span className={styles.historyLabel}>{t('profile.totalPoints')}</span>
              <span className={styles.historyValNeutral}>{xp}</span>
            </div>
          </div>

          <div className={styles.statsGrid}>
            {/* Default Stats */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>{t('profile.baseStats')}</h4>
              <ul className={styles.statList}>
                <li>
                  <span className={styles.statLabel}>❤️ {t('profile.baseHp')}</span>
                  <span className={styles.statVal}>{stats?.hp_max + (displayLevel * 10) + ((attributes.strength || 0) * 2.5) || '--'}</span>
                </li>
                <li>
                  <span className={styles.statLabel}>⭐ {t('profile.baseAp')}</span>
                  <span className={styles.statVal}>{stats?.ap_max + Math.floor(displayLevel / 5) + Math.floor((attributes.agility || 0) / 15) || '--'}</span>
                </li>
                <li>
                  <span className={styles.statLabel}>👟 {t('profile.baseMp')}</span>
                  <span className={styles.statVal}>{stats?.mp_max + (displayLevel * 5) + ((attributes.intelligence || 0) * 1.5) || '--'}</span>
                </li>
                <li>
                  <span className={styles.statLabel}>🎒 {t('profile.inventory')}</span>
                  <span className={styles.statVal}>{stats?.inventory_capacity ?? '20'} {t('profile.slots')}</span>
                </li>
              </ul>
            </div>

            {/* Combat Attributes */}
            <div className={styles.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h4 className={styles.sectionTitle} style={{ margin: 0 }}>{t('profile.characteristics')}</h4>
                {unspentPoints > 0 && (
                  <span style={{ fontSize: '0.8rem', background: '#e67e22', color: 'white', padding: '2px 8px', borderRadius: '12px' }}>
                    {unspentPoints} {t('profile.points' as any)}
                  </span>
                )}
              </div>
              <ul className={styles.statList}>
                {['strength', 'intelligence', 'agility', 'luck', 'wisdom', 'initiative', 'dodge', 'block'].map(attr => (
                  <li key={attr} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className={styles.statLabel}>{translateAttr(attr)}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className={styles.statVal}>{attributes[attr as keyof typeof attributes] || 0}</span>
                      {unspentPoints > 0 && (
                        <button 
                          onClick={() => handleUpgradeAttribute(attr)}
                          disabled={isUpgrading}
                          style={{ 
                            background: '#2ecc71', color: 'white', border: 'none', borderRadius: '4px', 
                            width: '20px', height: '20px', cursor: isUpgrading ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'
                          }}
                        >+</button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>,
    document.body
  );
}
